/**
 * Feature: Dynamic Bundle Price Updates
 * Listen for 'sectionheroes:bundle:update' to update product header prices.
 */

(function () {
  /**
   * The new-standard product template keeps the price in its own colour and shows
   * the saving as a percentage, so the markup rebuilt below differs there.
   */
  const isNewStandard = () => document.body.classList.contains('template-new-standard');

  /**
   * Updates the price display elements based on bundle state data.
   * @param {Object} data - The bundle state data from the event.
   */
  function updateBundlePriceDisplay(data) {
    if (!data) return;

    const priceContainers = document.querySelectorAll('.product-header_price');

    // Function to generate the inner HTML for the price container
    const generatePriceHTML = (data) => {
      const hasDiscount = data.compareAtPrice > data.price;
      const priceStyle = hasDiscount && !isNewStandard() ? 'style="color: var(--base-color-brand--red)"' : '';

      let html = `<div ${priceStyle}>${data.formattedPrice}</div>`;
      if (hasDiscount && data.formattedCompareAtPrice) {
        html += `
            <div class="product-header_compare-at-price">
              <div>${data.formattedCompareAtPrice}</div>
            </div>
          `;
      }
      return html;
    };

    if (priceContainers.length > 0) {
      priceContainers.forEach(container => {
        container.innerHTML = generatePriceHTML(data);
      });
    } else {
      // Fallback: If .product-header_price doesn't exist, try to populate it in the parent component
      const parentComponent = document.querySelector('.product-header_price-component');
      if (parentComponent) {
        const newPriceContainer = document.createElement('div');
        newPriceContainer.className = 'product-header_price';
        newPriceContainer.innerHTML = generatePriceHTML(data);

        // Insert before the shipping info text if it exists
        const shippingInfo = parentComponent.querySelector('.text-size-small.text-weight-bold');
        if (shippingInfo) {
          parentComponent.insertBefore(newPriceContainer, shippingInfo);
        } else {
          parentComponent.appendChild(newPriceContainer);
        }
      }
    }

    // Badge Update Logic
    const badgeContainers = document.querySelectorAll('.product-header_badges');
    const hasDiscount = data.compareAtPrice > data.price;

    const generateBadgeHTML = (data) => {
      const savings = data.compareAtPrice - data.price;
      const label = isNewStandard()
        ? `-${Math.round((savings / data.compareAtPrice) * 100)} %`
        : `Du sparst ${window.LiquifyHelper?.moneyFormat(savings) || `${(savings / 100).toFixed(2)} €`}`;

      return `
          <div class="product-header_badge-row">
            <div class="default_badge background-color-red is-product-header">
              <div>${label}</div>
            </div>
          </div>
        `;
    };

    if (hasDiscount) {
      if (badgeContainers.length > 0) {
        badgeContainers.forEach(container => {
          container.innerHTML = generateBadgeHTML(data);
          container.style.display = '';
        });
      } else {
        const parentComponent = document.querySelector('.product-header_price-component');
        if (parentComponent) {
          const newBadgeContainer = document.createElement('div');
          newBadgeContainer.className = 'product-header_badges';
          newBadgeContainer.innerHTML = generateBadgeHTML(data);
          parentComponent.prepend(newBadgeContainer);
        }
      }
    } else {
      badgeContainers.forEach(container => {
        container.innerHTML = '';
        container.style.display = 'none';
      });
    }

    // Also update banner prices if they exist (common pattern in this theme)
    const bannerPriceContainers = document.querySelectorAll('.product-banner_price-wrapper');
    bannerPriceContainers.forEach(container => {
      const hasDiscount = data.compareAtPrice > data.price;
      const priceStyle = hasDiscount ? 'style="color: var(--base-color-brand--red)"' : '';

      let html = `<div class="product-banner_price" ${priceStyle}>${data.formattedPrice}</div>`;
      if (hasDiscount && data.formattedCompareAtPrice) {
        html += `
              <div class="product-banner_compare-at-price">
                <div>${data.formattedCompareAtPrice}</div>
              </div>
            `;
      }
      container.innerHTML = html;
    });
  }

  // Listen for custom bundle update events
  document.addEventListener('sectionheroes:bundle:update', (e) => {
    updateBundlePriceDisplay(e.detail);
  });

  // Handle case where bundle state was already set before this script loaded
  if (window.sectionheroes?.bundleState) {
    updateBundlePriceDisplay(window.sectionheroes.bundleState);
  }
})();

/**
 * Feature: "Du sparst …" line inside each Section Heroes bundle offer.
 *
 * The offers are rendered by the app, which has no setting for this line, so it
 * is injected client side on the new-standard template only. The app re-renders
 * the offer list when the shopper picks a different one, hence the observer.
 */
(function () {
  const MARKER = 'sh-bundle-saving';
  const LABEL = 'Du sparst';

  const formatMoney = (cents) => {
    if (window.LiquifyHelper && typeof window.LiquifyHelper.moneyFormat === 'function') {
      return window.LiquifyHelper.moneyFormat(cents);
    }
    return `${(cents / 100).toFixed(2).replace('.', ',')} €`;
  };

  /**
   * The app exposes the offer maths as data attributes, which avoids having to
   * parse the localised price strings it renders.
   */
  const finalPrice = (block) => {
    const base = parseInt(block.dataset.basePrice, 10) || 0;
    const type = block.dataset.discountType;
    const value = parseFloat(block.dataset.discountValue) || 0;

    if (!type || type === 'none' || !value) return base;
    if (type === 'percentage') return Math.round(base * (1 - value / 100));
    return Math.max(0, base - Math.round(value * 100));
  };

  function render() {
    document.querySelectorAll('.sh-bundle-block').forEach((block) => {
      const info = block.querySelector('.sh-bundle-block-content-item-product-content-info');
      if (!info) return;

      const existing = info.querySelector(`.${MARKER}`);
      const compare = parseInt(block.dataset.baseComparePrice, 10) || 0;
      const savings = compare - finalPrice(block);
      const isOffer = block.dataset.discountType && block.dataset.discountType !== 'none';

      if (!isOffer || savings <= 0) {
        if (existing) existing.remove();
        return;
      }

      const text = `${LABEL} ${formatMoney(savings)}`;
      if (existing) {
        if (existing.textContent !== text) existing.textContent = text;
        return;
      }

      const node = document.createElement('div');
      node.className = MARKER;
      node.textContent = text;
      info.appendChild(node);
    });
  }

  function schedule(fn) {
    let ticking = false;
    return () => {
      if (ticking) return;
      ticking = true;
      requestAnimationFrame(() => {
        ticking = false;
        fn();
      });
    };
  }

  function bindBundleObserver(onChange) {
    const root = document.querySelector('sh-bundle') || document.querySelector('.sh-bundle-layout');
    if (!root || root.dataset.shSavingObserved) return;
    root.dataset.shSavingObserved = '1';
    new MutationObserver(onChange).observe(root, { childList: true, subtree: true });
  }

  function init() {
    if (!document.body.classList.contains('template-new-standard')) return;

    const onChange = schedule(render);
    render();
    bindBundleObserver(onChange);
    document.addEventListener('sectionheroes:bundle:update', onChange);
    document.addEventListener('shopify:section:load', () => {
      render();
      bindBundleObserver(onChange);
    });
    [800, 2500].forEach((ms) => {
      setTimeout(() => bindBundleObserver(onChange), ms);
    });
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', init);
  } else {
    init();
  }
})();

/**
 * Feature: "Auswahl: {bundle name}" line above the Section Heroes bundle.
 * The offer title lives in the app markup, so we copy the selected one.
 */
(function () {
  const NAME_SEL = '[data-sh-bundle-selection-name]';
  const UNIT_SEL = '[data-sh-bundle-selection-unit]';
  const DATA_SEL = '[data-sh-bundle-unit-data]';
  const BLOCK_SEL = '.sh-bundle-selection-block';
  const TITLE_SEL = '.sh-bundle-block-content-item-product-content-info-title';

  const selectedTitle = () => {
    const fromState = window.sectionheroes && window.sectionheroes.bundleState && window.sectionheroes.bundleState.offer
      ? String(window.sectionheroes.bundleState.offer.name || '').trim()
      : '';
    if (fromState) return fromState;

    const fromName = document.querySelector('[data-sh-bundle-selection-name]');
    if (fromName && fromName.textContent.trim()) return fromName.textContent.trim();

    const selected =
      document.querySelector(`.sh-bundle-block-content.selected ${TITLE_SEL}`) ||
      document.querySelector(`.sh-bundle-block-content[aria-checked="true"] ${TITLE_SEL}`);
    return selected ? selected.textContent.trim() : '';
  };

  window.shSelectedBundleTitle = selectedTitle;

  const selectedVariantId = () => {
    const scope =
      document.querySelector('#product-header') ||
      document.querySelector('.product-header_trigger') ||
      document.querySelector('.product-header_product-details') ||
      document;
    const input = scope.querySelector('[name="id"]');
    return input && input.value ? String(input.value) : '';
  };

  const wrapOverride = (value) => {
    const inner = String(value || '')
      .replace(/^\(+/, '')
      .replace(/\)+$/, '')
      .trim();
    return inner ? '(' + inner + ')' : '';
  };

  const slashUnit = (unit) => String(unit || '').replace(/^\s*pro\s+/i, '').trim();

  const spacedEuro = (money) => String(money || '').replace(/\s*€/g, ' €').trim();

  const unitData = () => {
    const node = document.querySelector(DATA_SEL);
    if (!node) return null;
    try {
      return JSON.parse(node.textContent);
    } catch (error) {
      return null;
    }
  };

  const selectedBundleBlock = () => {
    const content =
      document.querySelector('.sh-bundle-block-content.selected') ||
      document.querySelector('.sh-bundle-block-content[aria-checked="true"]');
    if (content) return content.closest('.sh-bundle-block');
    return document.querySelector('.sh-bundle-block');
  };

  const offerPriceCents = (block) => {
    if (!block) return 0;
    const base = parseInt(block.dataset.basePrice, 10) || 0;
    const type = block.dataset.discountType;
    const value = parseFloat(block.dataset.discountValue) || 0;
    if (!type || type === 'none' || !value) return base;
    if (type === 'percentage') return Math.round(base * (1 - value / 100));
    return Math.max(0, base - Math.round(value * 100));
  };

  const packSizeFromTitle = (title) => {
    const match = String(title || '').match(/(\d+)\s*er/i);
    return match ? parseInt(match[1], 10) : 0;
  };

  const unitLabel = (data) => {
    if (!data) return '';

    const variants = data.variants || {};
    var id = selectedVariantId();
    var entry = variants[id];
    if (!entry) {
      var ids = Object.keys(variants);
      if (ids.length === 1) entry = variants[ids[0]];
    }

    const variantOverride = entry && entry.override ? String(entry.override).trim() : '';
    const productOverride = data.override ? String(data.override).trim() : '';
    if (variantOverride) return wrapOverride(variantOverride);
    if (productOverride) return wrapOverride(productOverride);

    const amount = entry ? parseInt(entry.amount, 10) : 0;
    if (!amount || amount === 1) return '';
    const unit = entry && entry.unit ? entry.unit : '';
    const block = selectedBundleBlock();
    const title = selectedTitle();
    var pieces = packSizeFromTitle(title);
    if (!pieces && block) {
      var qty = parseInt(block.dataset.quantity, 10) || 1;
      pieces = qty * (amount > 0 ? amount : 1);
    }
    if (!pieces) pieces = amount;
    if (!pieces || !unit) return '';

    const price = offerPriceCents(block) || livePriceCents(entry ? entry.price : 0);
    if (!price) return '';
    const cents = Math.round(price / pieces);
    if (!cents) return '';
    const unitName = slashUnit(unit);
    if (!unitName) return '';
    return '(Nur ' + spacedEuro(formatMoney(cents)) + ' pro ' + unitName + ')';
  };

  const livePriceCents = (fallback) => {
    try {
      if (window.Alpine && typeof window.Alpine.$data === 'function') {
        const root = document.querySelector('.product-header_price-component') ||
          document.querySelector('.product-header_product-details [x-data]');
        if (root) {
          const data = window.Alpine.$data(root);
          if (data && typeof data.price === 'number' && data.price > 0) return data.price;
        }
      }
    } catch (error) {
      /* fall back to the variant price from Liquid */
    }
    return fallback;
  };

  const formatMoney = (cents) => {
    if (window.LiquifyHelper && typeof window.LiquifyHelper.moneyFormat === 'function') {
      return window.LiquifyHelper.moneyFormat(cents);
    }
    return (cents / 100).toFixed(2).replace('.', ',') + ' €';
  };

  const syncVisibility = () => {
    const hasBundle = !!(
      document.querySelector('sh-bundle') ||
      document.querySelector('.sh-bundle-layout')
    );
    document.querySelectorAll(BLOCK_SEL).forEach((el) => {
      if (el.classList.contains('is-design-mode')) return;
      el.classList.toggle('is-visible', hasBundle);
      const wrap = el.closest('.shopify-block');
      if (wrap) wrap.style.display = hasBundle ? '' : 'none';
    });
  };

  const render = () => {
    syncVisibility();
    const name = selectedTitle();
    if (name) {
      document.querySelectorAll(NAME_SEL).forEach((el) => {
        if (el.textContent !== name) el.textContent = name;
      });
    }

    const label = unitLabel(unitData());
    document.querySelectorAll(UNIT_SEL).forEach((el) => {
      if (el.textContent !== label) el.textContent = label;
      const hide = !label;
      if (el.hidden !== hide) el.hidden = hide;
    });
  };

  const schedule = (() => {
    let ticking = false;
    return () => {
      if (ticking) return;
      ticking = true;
      requestAnimationFrame(() => {
        ticking = false;
        render();
      });
    };
  })();

  const bindBundleObserver = () => {
    const root = document.querySelector('sh-bundle') || document.querySelector('.sh-bundle-layout');
    if (!root || root.dataset.shSelectionObserved) return;
    root.dataset.shSelectionObserved = '1';
    new MutationObserver(schedule).observe(root, {
      childList: true,
      subtree: true,
      attributes: true,
      attributeFilter: ['class', 'aria-checked']
    });
  };

  const init = () => {
    if (!document.querySelector(BLOCK_SEL)) return;

    render();
    bindBundleObserver();
    document.addEventListener('sectionheroes:bundle:update', schedule);
    document.addEventListener('shopify:section:load', () => {
      render();
      bindBundleObserver();
    });
    [800, 2500].forEach((ms) => {
      setTimeout(() => {
        bindBundleObserver();
        schedule();
      }, ms);
    });
    document.addEventListener('change', (e) => {
      if (!e.target || !e.target.matches) return;
      if (
        e.target.matches('input[name="sh-bundle-offer"]') ||
        e.target.matches('[name="id"]')
      ) {
        schedule();
      }
    });
    document.addEventListener('click', (e) => {
      if (e.target && e.target.closest && e.target.closest('.sh-bundle-block')) {
        schedule();
      }
    });
  };

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', init);
  } else {
    init();
  }
})();

/**
 * Remember Section Heroes offer titles, quantities and prices so the cart
 * drawer and cart page can label each line from the real offer, not a
 * stale _bundle_set property.
 */
(function () {
  let tries = 0;
  const run = () => {
    if (!window.shBundleSet) {
      if (tries < 20) {
        tries += 1;
        window.setTimeout(run, 100);
      }
      return;
    }
    if (typeof window.shBundleSet.rememberFromDom === 'function') {
      window.shBundleSet.rememberFromDom();
    }
    if (typeof window.shBundleSet.paintCartPage === 'function') {
      window.shBundleSet.paintCartPage();
    }
  };

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', run);
  } else {
    run();
  }
  document.addEventListener('sectionheroes:bundle:update', run);
  document.addEventListener('shopify:section:load', run);
})();

/**
 * Feature: keep the USP grid outside the Section Heroes buy box card.
 *
 * <sh-bbw> does not wrap anything, it just puts its card class (white
 * background, radius, shadow, padding) on the product details column, so every
 * block in that column ends up inside the card -- including the USP grid, which
 * the design places below it on the page background. Everything above the USP
 * block is moved into an inner wrapper and the card class is handed over to it.
 */
(function () {
  const WRAPPER_CLASS = 'sh-buybox';

  function init() {
    if (!document.body.classList.contains('template-new-standard')) return;

    const details = document.querySelector('.product-header_product-details');
    if (!details) return;

    const bbw = details.querySelector('sh-bbw');
    if (!bbw && !details.querySelector('[id*="md_buybox_wrapper"]')) return;

    const content = details.closest('.product-header_content');
    if (content) content.classList.add('has-sh-buybox');

    if (details.querySelector(`:scope > .${WRAPPER_CLASS}`)) return;

    const usp = details.querySelector(':scope > .sh-usp-block');
    if (!usp || usp === details.firstElementChild) return;

    const wrapper = document.createElement('div');
    wrapper.className = WRAPPER_CLASS;
    details.insertBefore(wrapper, details.firstChild);

    let hops = 0;
    while (wrapper.nextSibling && wrapper.nextSibling !== usp && hops < 40) {
      wrapper.appendChild(wrapper.nextSibling);
      hops += 1;
    }

    const cardClass = bbw && bbw.dataset.cls;
    if (cardClass) {
      details.classList.remove(cardClass);
      wrapper.classList.add(cardClass);
    }

    const tabs = details.querySelector(':scope > .product-tabs_wrapper');
    if (tabs) details.appendChild(tabs);
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', init);
  } else {
    init();
  }

  document.addEventListener('shopify:section:load', init);
})();

document.addEventListener('crosstheme:cart:update', function (event) {
  // Optional: You can access the result of the add-to-cart request via event.detail
  // const result = event.detail;

  // Fire the custom events requested
  window.dispatchEvent(new CustomEvent('cartupdated'));
  window.dispatchEvent(new CustomEvent('toggleminicart'));
});

/**
 * SH – Review: use Reviews.io Card Carousel rating/count when present.
 * If a product page has no carousel, keep the section setting as fallback.
 */
(function () {
  const TARGET_SELECTOR = '[data-sh-review-stats]';
  const CAROUSEL_SELECTOR = 'reviewsio-card-carousel, [id^="reviewsio-carousel-widget"]';
  const WAIT_MS = 10000;

  function carouselRoot() {
    return document.querySelector(CAROUSEL_SELECTOR);
  }

  function readCarouselStats(root) {
    if (!root) return null;
    const header = root.querySelector('.CarouselWidget__header') || root;
    const nums = header.querySelectorAll('.cssVar-subheading__number');
    if (nums.length < 2) return null;
    const rating = (nums[0].textContent || '').trim();
    const count = (nums[1].textContent || '').trim();
    if (!rating || !count) return null;
    return { rating, count };
  }

  function applyStats(targets, stats) {
    targets.forEach((el) => {
      const template = el.getAttribute('data-sh-review-template') || '';
      if (!template) return;
      el.innerHTML = template
        .replace(/__RATING__/g, stats.rating)
        .replace(/__COUNT__/g, stats.count);
    });
  }

  function sync() {
    const targets = document.querySelectorAll(TARGET_SELECTOR);
    if (!targets.length) return;

    const root = carouselRoot();
    if (!root) return;

    const applyIfReady = () => {
      const stats = readCarouselStats(root);
      if (!stats) return false;
      applyStats(targets, stats);
      return true;
    };

    if (applyIfReady()) return;

    const observer = new MutationObserver(() => {
      if (applyIfReady()) observer.disconnect();
    });
    observer.observe(root, { childList: true, subtree: true, characterData: true });
    window.setTimeout(() => observer.disconnect(), WAIT_MS);
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', sync);
  } else {
    sync();
  }

  document.addEventListener('shopify:section:load', sync);
})();

/**
 * Product header review count: underline link jumps to Reviews.io Card Carousel.
 */
(function () {
  const ANCHOR_ID = 'reviewsio-carousel';
  const CAROUSEL_SELECTOR = 'reviewsio-card-carousel, [id^="reviewsio-carousel-widget"]';
  const SECTION_FALLBACK_ID = 'shopify-section-1737970946a58d4418';
  const WAIT_MS = 10000;

  function carouselSection() {
    return (
      document.getElementById(ANCHOR_ID) ||
      document.getElementById(SECTION_FALLBACK_ID) ||
      document.querySelector('[id*="1737970946a58d4418"]') ||
      document.querySelector(CAROUSEL_SELECTOR)?.closest('.shopify-section') ||
      document.querySelector(CAROUSEL_SELECTOR)
    );
  }

  function ensureAnchor() {
    if (document.getElementById(ANCHOR_ID)) return true;
    const section = carouselSection();
    if (!section) return false;
    const anchor = document.createElement('div');
    anchor.id = ANCHOR_ID;
    section.insertBefore(anchor, section.firstChild);
    return true;
  }

  function stickyHeaderOffset() {
    const nav = document.querySelector('.section_navbar') || document.querySelector('.nav_component');
    if (!nav) return 96;
    const rect = nav.getBoundingClientRect();
    return Math.ceil(Math.max(rect.bottom, rect.height, 0));
  }

  function scrollTarget() {
    const widget = document.querySelector(CAROUSEL_SELECTOR);
    if (widget) return widget.closest('.shopify-section') || widget;
    const anchor = document.getElementById(ANCHOR_ID);
    return (anchor && (anchor.closest('.shopify-section') || anchor)) || carouselSection();
  }

  function scrollToCarousel(event) {
    if (!ensureAnchor()) return;
    const el = scrollTarget();
    if (!el) return;
    event.preventDefault();
    const gap = 16;
    const top = el.getBoundingClientRect().top + window.pageYOffset - stickyHeaderOffset() - gap;
    window.scrollTo({ top: Math.max(0, top), behavior: 'smooth' });
  }

  function bindLinks() {
    document.querySelectorAll('a[href="#reviewsio-carousel"]').forEach((link) => {
      if (link.dataset.carouselBound === 'true') return;
      link.dataset.carouselBound = 'true';
      link.addEventListener('click', scrollToCarousel);
    });
  }

  function init() {
    if (!document.body.classList.contains('template-new-standard')) return;
    bindLinks();
    if (ensureAnchor()) return;

    const observer = new MutationObserver(() => {
      if (ensureAnchor()) observer.disconnect();
    });
    observer.observe(document.body, { childList: true, subtree: true });
    window.setTimeout(() => observer.disconnect(), WAIT_MS);
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', init);
  } else {
    init();
  }

  document.addEventListener('shopify:section:load', init);
})();

/**
 * Attach the selected SH-bundle offer title ("6er Set") as a hidden
 * line-item property so the cart drawer can show it under the title.
 */
(function () {
  const TITLE_KEY = '_bundle_set';
  const TITLE_SEL = '.sh-bundle-block-content-item-product-content-info-title';

  const selectedTitle = () => {
    if (typeof window.shSelectedBundleTitle === 'function') {
      return window.shSelectedBundleTitle() || '';
    }
    const selected =
      document.querySelector(`.sh-bundle-block-content.selected ${TITLE_SEL}`) ||
      document.querySelector(`.sh-bundle-block-content[aria-checked="true"] ${TITLE_SEL}`);
    return selected ? selected.textContent.trim() : '';
  };

  const bundleVariantIds = () => {
    return Array.from(document.querySelectorAll('.sh-bundle-block[data-variant-id]'))
      .map((el) => String(el.getAttribute('data-variant-id') || ''))
      .filter(Boolean);
  };

  const requestUrl = (input, init) => {
    if (typeof input === 'string') return input;
    if (input && typeof input.url === 'string') return input.url;
    if (init && typeof init.url === 'string') return init.url;
    return '';
  };

  const isCartAdd = (url) => /\/cart\/add(?:\.js)?(?:$|\?)/i.test(url || '');

  const itemId = (item) => String((item && (item.id || item.variant_id)) || '');

  const hasBundleTag = (item) => {
    const raw = item && item.properties && item.properties['_sh-bundle'];
    if (!raw) return false;
    try {
      const parsed = typeof raw === 'string' ? JSON.parse(raw) : raw;
      return !!(parsed && !parsed.g);
    } catch (error) {
      return true;
    }
  };

  const shouldTagItem = (item, ids, itemCount) => {
    if (!item) return false;
    if (hasBundleTag(item)) return true;
    if (ids.length) return ids.indexOf(itemId(item)) !== -1;
    return itemCount === 1;
  };

  const withTitle = (item, title, ids, itemCount) => {
    if (!shouldTagItem(item, ids, itemCount)) return item;
    const props = Object.assign({}, item.properties || {});
    if (props[TITLE_KEY]) return item;
    const fromOffer = window.shBundleSet && typeof window.shBundleSet.titleForAddItem === 'function'
      ? window.shBundleSet.titleForAddItem(item)
      : '';
    const use = fromOffer || title;
    if (!use) return item;
    props[TITLE_KEY] = use;
    return Object.assign({}, item, { properties: props });
  };

  const attachToJson = (data, title, ids) => {
    if (!data || typeof data !== 'object') return data;
    if (Array.isArray(data.items)) {
      data.items = data.items.map((item) => withTitle(item, title, ids, data.items.length));
      return data;
    }
    if (data.id) return withTitle(data, title, ids, 1);
    return data;
  };

  const attachToBody = (body, title, ids) => {
    if (body == null) return body;

    if (typeof FormData !== 'undefined' && body instanceof FormData) {
      if (!title) return body;
      const id = String(body.get('id') || '');
      if (body.get(`properties[${TITLE_KEY}]`)) return body;
      if (ids.length && id && ids.indexOf(id) === -1) return body;
      body.append(`properties[${TITLE_KEY}]`, title);
      return body;
    }

    if (typeof URLSearchParams !== 'undefined' && body instanceof URLSearchParams) {
      if (!title) return body;
      const id = String(body.get('id') || '');
      if (body.get(`properties[${TITLE_KEY}]`)) return body;
      if (ids.length && id && ids.indexOf(id) === -1) return body;
      body.append(`properties[${TITLE_KEY}]`, title);
      return body;
    }

    if (typeof body === 'string') {
      try {
        return JSON.stringify(attachToJson(JSON.parse(body), title, ids));
      } catch (error) {
        return body;
      }
    }

    return body;
  };

  const patchOutgoing = (url, body) => {
    if (!isCartAdd(url)) return body;
    return attachToBody(body, selectedTitle(), bundleVariantIds());
  };

  const origFetch = window.fetch;

  if (typeof origFetch === 'function') {
    window.fetch = function (input, init) {
      const url = requestUrl(input, init);
      if (!isCartAdd(url)) {
        return origFetch.apply(this, arguments);
      }

      const title = selectedTitle();
      const ids = bundleVariantIds();

      try {
        if (init && init.body != null) {
          return origFetch.call(this, input, Object.assign({}, init, {
            body: attachToBody(init.body, title, ids)
          }));
        }
        if (typeof Request !== 'undefined' && input instanceof Request) {
          return input.clone().text().then((text) => {
            return origFetch.call(this, new Request(input, {
              body: attachToBody(text, title, ids)
            }));
          });
        }
      } catch (error) {
        return origFetch.apply(this, arguments);
      }

      return origFetch.apply(this, arguments);
    };
  }

  const origOpen = XMLHttpRequest.prototype.open;
  const origSend = XMLHttpRequest.prototype.send;
  XMLHttpRequest.prototype.open = function (method, url) {
    this._shBundleCartUrl = url;
    return origOpen.apply(this, arguments);
  };
  XMLHttpRequest.prototype.send = function (body) {
    try {
      body = patchOutgoing(this._shBundleCartUrl || '', body);
    } catch (error) {
      // Keep the original body if patching fails.
    }
    return origSend.call(this, body);
  };
})();
